import { useMemo, useState } from 'react'
import { api } from '../lib/api'
import { useAuth } from '../lib/auth'
import { useAsync } from '../lib/useAsync'
import type { Comment, CommentNode } from '../lib/types'
import { Button, Spinner, cx } from './ui'

/** Fold the flat `parent_id` list into a reply tree, oldest first. */
function toTree(flat: Comment[]): CommentNode[] {
  const byId = new Map<number, CommentNode>(flat.map((c) => [c.id, { ...c, replies: [] }]))
  const roots: CommentNode[] = []

  for (const c of byId.values()) {
    const parent = c.parent_id === null ? undefined : byId.get(c.parent_id)
    // A reply whose parent was deleted still deserves to be shown, at top level.
    if (parent) parent.replies.push(c)
    else roots.push(c)
  }

  const sort = (list: CommentNode[]) => {
    list.sort((a, b) => a.created_at.localeCompare(b.created_at))
    list.forEach((c) => sort(c.replies))
  }
  sort(roots)
  return roots
}

const when = (iso: string) =>
  new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })

export function Comments({ docId }: { docId: number }) {
  const { user, signIn } = useAuth()
  const { data, loading, error } = useAsync((signal) => api.comments(docId, signal), [docId])
  const [added, setAdded] = useState<Comment[]>([])

  const tree = useMemo(() => toTree([...(data ?? []), ...added]), [data, added])
  const count = (data?.length ?? 0) + added.length

  return (
    <section className="mt-16 border-t border-line pt-8">
      <h2 className="mb-6 text-lg font-semibold text-ink">
        Discussion{' '}
        {count > 0 && <span className="text-subtle">({count})</span>}
      </h2>

      {user ? (
        <CommentForm docId={docId} onAdded={(c) => setAdded((a) => [...a, c])} />
      ) : (
        <div className="rounded-[var(--radius-sh)] border border-line bg-sunken px-4 py-3 text-sm text-muted">
          <button onClick={() => void signIn()} className="font-medium text-accent-text hover:underline">
            Sign in
          </button>{' '}
          to join the discussion.
        </div>
      )}

      <div className="mt-8 space-y-6">
        {loading && <Spinner label="Loading comments…" />}
        {error && <p className="text-sm text-danger">Could not load comments.</p>}
        {!loading && !error && tree.length === 0 && (
          <p className="text-sm text-subtle">No comments yet.</p>
        )}
        {tree.map((c) => (
          <CommentItem key={c.id} node={c} docId={docId} onAdded={(x) => setAdded((a) => [...a, x])} />
        ))}
      </div>
    </section>
  )
}

function CommentItem({
  node,
  docId,
  onAdded,
  depth = 0,
}: {
  node: CommentNode
  docId: number
  onAdded: (c: Comment) => void
  depth?: number
}) {
  const { user } = useAuth()
  const [replying, setReplying] = useState(false)

  return (
    <article className={cx(depth > 0 && 'border-l border-line pl-4')}>
      <header className="flex items-baseline gap-2 text-xs">
        <span className="font-medium text-ink">User #{node.user_id}</span>
        <time className="text-subtle" dateTime={node.created_at}>
          {when(node.created_at)}
        </time>
      </header>

      <p className="mt-1.5 text-sm whitespace-pre-wrap text-muted">{node.body}</p>

      {user && depth < 3 && (
        <button
          onClick={() => setReplying((r) => !r)}
          className="mt-2 text-xs font-medium text-subtle hover:text-accent-text"
        >
          {replying ? 'Cancel' : 'Reply'}
        </button>
      )}

      {replying && (
        <div className="mt-3">
          <CommentForm
            docId={docId}
            parentId={node.id}
            compact
            onAdded={(c) => {
              onAdded(c)
              setReplying(false)
            }}
          />
        </div>
      )}

      {node.replies.length > 0 && (
        <div className="mt-5 space-y-5">
          {node.replies.map((r) => (
            <CommentItem key={r.id} node={r} docId={docId} onAdded={onAdded} depth={depth + 1} />
          ))}
        </div>
      )}
    </article>
  )
}

function CommentForm({
  docId,
  parentId,
  compact,
  onAdded,
}: {
  docId: number
  parentId?: number
  compact?: boolean
  onAdded: (c: Comment) => void
}) {
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    const text = body.trim()
    if (!text || busy) return

    setBusy(true)
    setError(null)
    try {
      onAdded(await api.addComment(docId, text, parentId))
      setBody('')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not post the comment.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={submit}>
      <textarea
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={compact ? 2 : 3}
        placeholder={parentId ? 'Write a reply…' : 'Add to the discussion…'}
        className="w-full resize-y rounded-[var(--radius-sh)] border border-line bg-surface px-3 py-2 text-sm text-ink placeholder:text-subtle focus:border-accent focus:outline-none"
      />
      {error && <p className="mt-1 text-xs text-danger">{error}</p>}
      <div className="mt-2 flex justify-end">
        <Button type="submit" variant="primary" size="sm" disabled={!body.trim() || busy}>
          {busy ? 'Posting…' : parentId ? 'Reply' : 'Comment'}
        </Button>
      </div>
    </form>
  )
}
